import { type ReactNode } from 'react';
import {
  MdChevronLeft,
  MdChevronRight,
  MdFirstPage,
  MdLastPage,
} from 'react-icons/md';

/**
 * Shared pagination footer for every CRUD grid screen. Lives in `components/ui/`
 * because the same exact markup was duplicated across all 10 grid screens —
 * the textbook "fourth repetition" extraction trigger from ARCHITECTURE.md.
 *
 * Responsive behavior:
 * - Below `sm` (480px): rows stack — "Showing X–Y of Z" sits above the page
 *   controls. First / Last buttons hide because Prev/Next + "X / Y" is all
 *   that's needed at phone width.
 * - `sm` and up: original side-by-side layout with all four navigation buttons.
 */

interface Props {
  page: number;
  pageSize: number;
  totalRecords: number;
  onPageChange: (page: number) => void;
  /**
   * Optional content appended after "Showing X–Y of Z" with a "·" separator.
   * Used by MarketPricesScreen to show the active date-range label.
   * Hidden when totalRecords === 0.
   */
  extra?: ReactNode;
}

export function DataPagination({
  page,
  pageSize,
  totalRecords,
  onPageChange,
  extra,
}: Props) {
  const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));

  return (
    <div className="mt-4 flex flex-col items-stretch gap-2 px-1 sm:flex-row sm:items-center sm:justify-between">
      <span className="text-[12.5px] text-foreground-muted">
        {totalRecords > 0 ? (
          <>
            Showing{' '}
            <span className="font-semibold text-foreground">
              {(page - 1) * pageSize + 1}–
              {Math.min(page * pageSize, totalRecords)}
            </span>{' '}
            of{' '}
            <span className="font-semibold text-foreground">{totalRecords}</span>
            {extra && (
              <>
                <span className="mx-2 text-border-strong">·</span>
                <span>{extra}</span>
              </>
            )}
          </>
        ) : (
          'No records'
        )}
      </span>

      <div className="inline-flex items-center gap-1 self-end rounded-full border border-border bg-surface px-1.5 py-1 shadow-soft sm:self-auto">
        <PgButton
          disabled={page <= 1}
          onClick={() => onPageChange(1)}
          ariaLabel="First page"
          className="hidden sm:flex"
        >
          <MdFirstPage size={16} />
        </PgButton>
        <PgButton
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          ariaLabel="Previous page"
        >
          <MdChevronLeft size={16} />
        </PgButton>
        <span className="px-2 text-[12.5px] tabular-nums text-foreground-muted">
          <span className="font-semibold text-foreground">{page}</span>
          <span className="mx-1 text-border-strong">/</span>
          <span>{totalPages}</span>
        </span>
        <PgButton
          disabled={page >= totalPages}
          onClick={() => onPageChange(page + 1)}
          ariaLabel="Next page"
        >
          <MdChevronRight size={16} />
        </PgButton>
        <PgButton
          disabled={page >= totalPages}
          onClick={() => onPageChange(totalPages)}
          ariaLabel="Last page"
          className="hidden sm:flex"
        >
          <MdLastPage size={16} />
        </PgButton>
      </div>
    </div>
  );
}

interface PgButtonProps {
  disabled: boolean;
  onClick: () => void;
  ariaLabel: string;
  children: ReactNode;
  className?: string;
}

function PgButton({
  disabled,
  onClick,
  ariaLabel,
  children,
  className,
}: PgButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      aria-label={ariaLabel}
      className={[
        'flex h-6 w-6 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/[0.1] disabled:cursor-not-allowed disabled:text-border-strong disabled:hover:bg-transparent',
        className ?? '',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
